/**
 * Source viewer drawer — a right slide-over over a dimmed app. Lands on the cited
 * chunk (set via state, not scrollIntoView), with a chunk navigator (prev/next +
 * "Cited · NN%" badge), the optional "What this chunk says" digest, and the chunk
 * text rebuilt into readable paragraphs — the cited chunk's body highlighted.
 *
 * Rendered via a portal (no chassis Dialog primitive); closes on Escape / backdrop.
 */

import { Badge } from "@d12labs/ui/badge";
import { Button } from "@d12labs/ui/button";
import { ChevronLeft, ChevronRight, ExternalLink, RefreshCw, X } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useDocumentPreview } from "../hooks/use-kb";
import { DOC_TYPE_META, timeAgo } from "../kb-ui";
import type { DocType } from "../types";
import { ChunkDigest } from "./chunk-digest";

/** Split a raw chunk into display paragraphs (blank-line separated, trimmed). */
function toParagraphs(content: string): string[] {
	return content
		.split(/\n{2,}/)
		.map((p) => p.trim())
		.filter(Boolean);
}

export function SourceDrawer({
	documentId,
	citedChunkIndex,
	citedScore,
	onClose,
}: {
	documentId: string;
	/** The chunk a citation pointed at — where the drawer lands + the "cited" one. */
	citedChunkIndex: number;
	citedScore?: number;
	onClose: () => void;
}) {
	const { data, isLoading, isError, error } = useDocumentPreview(documentId);
	const [chunkIndex, setChunkIndex] = useState(citedChunkIndex);

	// Re-land on the cited chunk whenever a new citation opens the (already-mounted) drawer.
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset-on-citation pattern
	useEffect(() => setChunkIndex(citedChunkIndex), [documentId, citedChunkIndex]);

	// Escape closes; lock body scroll while open.
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKey);
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			window.removeEventListener("keydown", onKey);
			document.body.style.overflow = prev;
		};
	}, [onClose]);

	const chunks = data?.chunks ?? [];
	const total = chunks.length;
	// Clamp the navigator to what actually came back.
	const safeIndex = total > 0 ? Math.min(chunkIndex, total - 1) : 0;
	const current = chunks.find((c) => c.chunkIndex === safeIndex) ?? chunks[safeIndex];
	const isCited = current?.chunkIndex === citedChunkIndex;
	const meta = DOC_TYPE_META[(data?.document.docType ?? "other") as DocType];
	const TypeIcon = meta.icon;

	return createPortal(
		<div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
			<button
				type="button"
				aria-label="Close source viewer"
				className="absolute inset-0 bg-black/50"
				onClick={onClose}
			/>
			<div className="relative flex h-full w-full max-w-[680px] flex-col border-l border-hairline-strong bg-canvas shadow-2xl">
				{/* Header */}
				<header className="flex items-start gap-3 border-b border-hairline px-5 py-4">
					<span
						className="mt-0.5 flex size-8 flex-none items-center justify-center rounded-md bg-surface-2"
						style={{ color: meta.tint }}
					>
						<TypeIcon className="size-4" />
					</span>
					<div className="min-w-0 flex-1">
						<h2 className="truncate text-body font-medium text-foreground">
							{data?.document.filename ?? "Loading…"}
						</h2>
						{data ? (
							<div className="mt-0.5 flex items-center gap-1.5 text-meta text-ink-subtle">
								<span>{meta.label}</span>
								<span>·</span>
								<span>{data.document.chunkCount} chunks</span>
								{data.document.ingestedAt ? (
									<>
										<span>·</span>
										<span>indexed {timeAgo(data.document.ingestedAt)}</span>
									</>
								) : null}
							</div>
						) : null}
					</div>
					{data?.originalUrl ? (
						<a href={data.originalUrl} target="_blank" rel="noreferrer">
							<Button variant="secondary" size="sm">
								<ExternalLink className="size-3.5" />
								Open original
							</Button>
						</a>
					) : null}
					<Button variant="ghost" size="icon-sm" onClick={onClose} aria-label="Close">
						<X className="size-4" />
					</Button>
				</header>

				{/* Chunk navigator */}
				<div className="flex items-center gap-2 border-b border-hairline px-5 py-2.5">
					<span className="text-meta text-ink-subtle">Chunk</span>
					<div className="flex items-center rounded-md border border-hairline bg-surface-1">
						<button
							type="button"
							aria-label="Previous chunk"
							disabled={safeIndex <= 0}
							onClick={() => setChunkIndex((i) => Math.max(0, i - 1))}
							className="flex size-6 items-center justify-center text-ink-subtle hover:text-foreground disabled:opacity-40"
						>
							<ChevronLeft className="size-3.5" />
						</button>
						<span className="px-1.5 font-mono text-caption text-foreground">
							{total > 0 ? safeIndex + 1 : 0} <span className="text-ink-tertiary">/ {total}</span>
						</span>
						<button
							type="button"
							aria-label="Next chunk"
							disabled={total === 0 || safeIndex >= total - 1}
							onClick={() => setChunkIndex((i) => Math.min(total - 1, i + 1))}
							className="flex size-6 items-center justify-center text-ink-subtle hover:text-foreground disabled:opacity-40"
						>
							<ChevronRight className="size-3.5" />
						</button>
					</div>
					{isCited ? (
						<Badge tone="primary" dot="var(--color-primary)">
							Cited{citedScore != null ? ` · ${citedScore}%` : ""}
						</Badge>
					) : null}
				</div>

				{/* Digest (optional) + full text */}
				<div className="min-h-0 flex-1 overflow-y-auto">
					{isLoading ? (
						<div className="flex items-center gap-2 px-5 py-4 text-body-sm text-ink-subtle">
							<RefreshCw className="size-3.5 animate-spin" />
							Loading document…
						</div>
					) : isError ? (
						<p className="px-5 py-4 text-body-sm text-destructive">{(error as Error).message}</p>
					) : current ? (
						<>
							<ChunkDigest summary={current.summary} keyFacts={current.keyFacts} />
							<div className="px-5 py-4">
								<div className="mb-3 flex items-center justify-between">
									<span className="text-eyebrow uppercase text-ink-subtle">Full text</span>
									{isCited ? (
										<span className="text-meta text-ink-subtle">cited passage highlighted</span>
									) : null}
								</div>
								<div
									className={
										isCited
											? "space-y-3 rounded-md bg-primary/[0.06] px-3 py-3 text-body-sm leading-relaxed text-ink-muted ring-1 ring-primary/25"
											: "space-y-3 text-body-sm leading-relaxed text-ink-muted"
									}
								>
									{toParagraphs(current.content).map((p, i) => (
										// biome-ignore lint/suspicious/noArrayIndexKey: stable paragraph order within a chunk
										<p key={i} className="whitespace-pre-wrap">
											{p}
										</p>
									))}
								</div>
							</div>
						</>
					) : (
						<p className="px-5 py-4 text-body-sm text-ink-subtle">This chunk is empty.</p>
					)}
				</div>
			</div>
		</div>,
		document.body,
	);
}
